import { AxiosError } from "axios";
import { FolderOpen } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { routes } from "@/configs/router.config/routes";
import { useAgentStream } from "@/hooks/agent/useAgentStream";
import { useThreadEventHandler } from "@/hooks/agent/useThreadEventHandler";
import { useThreadMessagesInfinite } from "@/hooks/agent/useThreadMessagesInfinite";
import { cn } from "@/lib/utils";
import { getAgentThreadStore } from "@/stores/agent/agentThreadStore";
import AgentChatInput from "./AgentChatInput";
import { Button } from "./Button";
import MessageListUI from "./chatMessages/MessageListUI";
import { FileBrowserModal } from "./FileBrowserModal";
import TaskProgressDisplay from "./TaskProgressDisplay";
import { ToolPanel } from "./ToolPanel";

interface AgentChatContainerUIProps {
  className?: string;
}

const FileBrowserToggleButton: React.FC<{ threadId: string; }> = ({ threadId }) => {
  const isOpenToolPanel = getAgentThreadStore(threadId)((state) => state.isOpenToolPanel);
  const setIsOpenFileBrowser = getAgentThreadStore(threadId)((state) => state.setIsOpenFileBrowser);

  return (
    <div
      className={cn(
        "fixed top-28 right-3 z-50 transition-all duration-300",
        isOpenToolPanel ? "opacity-0 pointer-events-none" : "opacity-100",
      )}
    >
      <Button
        onClick={() => setIsOpenFileBrowser(true)}
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
      >
        <FolderOpen className="h-4 w-4 text-gray-600" />
      </Button>
    </div>
  );
};

const AgentChatContainerUI: React.FC<AgentChatContainerUIProps> = ({ className }) => {
  const { workspaceId, threadId } = useParams();
  const navigate = useNavigate();
  const messageListRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const setHasScrollSpacer = getAgentThreadStore(threadId)((state) => state.setHasScrollSpacer);
  const selectedToolCallMessageId = getAgentThreadStore(threadId)((state) => state.selectedToolCallMessageId);

  const {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    error,
  } = useThreadMessagesInfinite(threadId);

  // 削除済みスレッドへのアクセス時にエージェント一覧へリダイレクト
  useEffect(() => {
    if (error instanceof AxiosError && error.response?.status === 404 && workspaceId) {
      navigate(routes.agent(workspaceId));
    }
  }, [error, workspaceId, navigate]);

  const latestTaskList = useMemo(
    () => messages.find((m) => m.writeTasksList)?.writeTasksList ?? null,
    [messages],
  );

  const selectedToolCallMessage = useMemo(
    () => messages.find((message) => message.id === selectedToolCallMessageId) ?? null,
    [messages, selectedToolCallMessageId],
  );

  const currentToolResponseMessage = useMemo(
    () =>
      selectedToolCallMessage?.toolCall?.id
        ? messages.find((m) => m.role === "tool_response" && m.toolResult?.id === selectedToolCallMessage.toolCall?.id)
          ?? null
        : null,
    [messages, selectedToolCallMessage],
  );

  const { onThreadEvent } = useThreadEventHandler(threadId);

  // SSEストリーミング
  useAgentStream(threadId, onThreadEvent);

  return (
    <div className={cn("flex h-full", className)}>
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col justify-center relative">
        <div className="flex-1 flex flex-col w-full h-full">
          {/* Messages Area */}
          <ScrollArea className="h-full" viewportRef={messageListRef}>
            <div className="max-w-[768px] px-4 mx-auto">
              {/* メッセージリスト */}
              {threadId && (
                <MessageListUI
                  threadId={threadId}
                  messages={messages}
                  isLoading={isLoading}
                  isLoadingMore={isLoadingMore}
                  hasMore={hasMore}
                  loadMore={loadMore}
                  scrollRef={messageListRef}
                  messagesEndRef={messagesEndRef}
                />
              )}
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>

          {/* Task Progress - inputボックスの上 */}
          {threadId && latestTaskList && latestTaskList.length > 0 && (
            <div className="px-4 pb-2 max-w-[768px] mx-auto w-full">
              <TaskProgressDisplay tasks={latestTaskList} />
            </div>
          )}

          {/* Input Area */}
          <div className="px-4 pb-4 max-w-[768px] mx-auto w-full">
            <AgentChatInput
              onSend={() => {
                setHasScrollSpacer(true);
                requestAnimationFrame(() => {
                  messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                });
              }}
            />
          </div>
        </div>
      </div>

      {/* Utility Icons */}
      {threadId && <FileBrowserToggleButton threadId={threadId} />}

      {/* Tool Panel */}
      {threadId && (
        <ToolPanel
          threadId={threadId}
          selectedToolCallMessage={selectedToolCallMessage}
          currentToolResponseMessage={currentToolResponseMessage}
        />
      )}

      {/* File Browser Modal */}
      {threadId && <FileBrowserModal threadId={threadId} />}
    </div>
  );
};

export default AgentChatContainerUI;
